import React, { useState, useEffect, useRef } from "react";

import { Appearance, Box, Button, Callout, Heading, Icon, Text, Table } from "@blasterjs/core";

import {
  AdjudicationMode,
  AdjudicationParadigm,
  CaseStatus,
  CaseWithImages,
  formatCaseStatus,
  ImageAndQueries,
  ImageAndQuery,
  isHeStain,
  QueryStatus
} from "../models";
import styled from "styled-components";
import Timestamp from "./Timestamp";
import { useAppDispatch, useAppSelector, useIsForcedAdjudicator } from "../hooks";
import { LoggedInUser, RolePermissions } from "../permissions";
import ConfirmationDialog from "./ConfirmationDialog";
import {
  transitionCaseStatus,
  completeRead,
  toggleSidebarExpanded
} from "../slices/caseImageViewer";
import { openEditCaseDialog } from "../slices/caseDialog";
import { CollapseIcon } from "./Expandable";
import Avatar from "./Avatar";
import ImageInfo from "./ImageInfo";
import TooltipFlyout, { TooltipLink } from "./Tooltip";
import CaseStatusDialog from "./CaseStatusDialog";
import PutOnHoldCaseDialog from "./PutOnHoldCaseDialog";
import RemoveHoldCaseDialog from "./RemoveHoldCaseDialog";
import { openCaseStatusDialog } from "../slices/caseStatusDialog";
import { openPutOnHoldCaseDialog } from "../slices/putOnHoldCaseDialog";
import { openRemoveHoldCaseDialog } from "../slices/removeHoldCaseDialog";
import { refreshCase } from "../slices/caseImageViewer";
import RedLabel from "./RedLabel";
import ImageQualityAssessmentForm from "./ImageQualityAssessmentForm";

interface Props {
  readonly studyName: string | null;
  readonly histoCase: CaseWithImages;
  readonly selectedImage: ImageAndQuery | null;
  readonly loggedInUser: LoggedInUser;
  readonly hasAiAnnotations: boolean;
  readonly readerAcceptsAi: boolean;
}

const CompactTable = styled(Table)`
  width: 100%;
  word-break: break-word;
  margin-bottom: 1rem;
  td:first-child {
    font-weight: 700;
    color: #231F20;
    font-size: 16px
    padding-right: 1rem;
    width: 12rem;
  }
  td:nth-child(2) {
    font-weight: 400;
    color: #231F20;
    font-size: 16px
    padding-right: 1rem;
    width: 12rem;
    padding-left: 2rem;
  }
`;

const SidebarPanelCase = ({
  studyName,
  histoCase,
  selectedImage,
  loggedInUser,
  hasAiAnnotations,
  readerAcceptsAi
}: Props) => {
  const dispatch = useAppDispatch();
  const studyView = useAppSelector(state => state.studies.study);
  const isForcedAdjudicator = useIsForcedAdjudicator();
  const putOnHoldDialog = useAppSelector(state => state.putOnHoldCaseDialog);
  const removeHoldDialog = useAppSelector(state => state.removeHoldCaseDialog);
  const caseStatusDialogState = useAppSelector(state => state.caseStatusDialog);
  const deficientFeatureOptions = useAppSelector(
    state => state.caseImageViewer.deficientFeatureOptions
  );
  const imageQualityAssessment = useAppSelector(
    state => state.caseImageViewer.imageQualityAssessment
  );

  // Refresh the case once a dialog that can change it (put on hold, remove hold, case status)
  // closes. Deliberately not on mount: the case and its first image have just been loaded by
  // `caseFetch`, and `refreshCase` would fetch the case, its biopsies and the selected image
  // (the whole annotation set) a second time.
  const isCaseDialogOpen =
    putOnHoldDialog.isOpen || removeHoldDialog.isOpen || caseStatusDialogState.isOpen;
  const wasCaseDialogOpen = useRef(false);
  useEffect(() => {
    if (wasCaseDialogOpen.current && !isCaseDialogOpen) {
      dispatch(refreshCase());
    }
    // eslint-disable-next-line functional/immutable-data
    wasCaseDialogOpen.current = isCaseDialogOpen;
  }, [isCaseDialogOpen, dispatch]);

  const [isCaseConfirmationDialogOpen, setCaseConfirmationDialogOpen] = useState(false);
  const [isCompleteRead, setIsCompleteRead] = useState(false);
  // Opens the QC-transition confirmation (Pass QC). The mark-as-scored path uses onCompleteRead.
  const onTransition = () => {
    setIsCompleteRead(false);
    setCaseConfirmationDialogOpen(true);
  };
  const onCompleteRead = () => {
    setIsCompleteRead(true);
    setCaseConfirmationDialogOpen(true);
  };
  const onCaseQCConfirm = () => {
    setCaseConfirmationDialogOpen(false);
    if (isCompleteRead) {
      dispatch(completeRead());
    } else {
      dispatch(transitionCaseStatus(histoCase.caseWithStatus));
    }
  };
  const onCaseQCCancel = () => {
    setCaseConfirmationDialogOpen(false);
  };

  // When completing a read on a case with imported AI annotations, the confirmation reflects
  // whether those counts are included in the reader's scoring (driven by whether THIS reader
  // currently accepts AI on the open image — readerAcceptsAi). Otherwise it is a plain
  // mark-as-scored confirmation.
  const completeReadTitle = hasAiAnnotations
    ? readerAcceptsAi
      ? "Mark as Scored with Imported Annotations"
      : "Mark as Scored without Imported Annotations"
    : "Mark as Scored";
  const completeReadMessage = hasAiAnnotations
    ? readerAcceptsAi
      ? "Are you sure you want to include the Imported AI Annotation Counts in your scoring? Annotations will be locked."
      : "Are you sure you want to exclude the Imported AI Annotation Counts in your scoring? Annotations will be locked."
    : "Are you sure you want to mark this case as scored? Annotations will be locked.";
  const confirmCaseQCTransitionDialog = (
    <ConfirmationDialog
      title={isCompleteRead ? completeReadTitle : "Confirm Case Status Change"}
      message={
        isCompleteRead ? completeReadMessage : "Are you sure you want to change this case's status?"
      }
      isOpen={isCaseConfirmationDialogOpen}
      onConfirm={onCaseQCConfirm}
      onCancel={onCaseQCCancel}
    />
  );

  const caseQcButton =
    loggedInUser.can([RolePermissions.AP_CaseViewer_ViewButtonPassISCQC]) &&
    histoCase.caseWithStatus.status === CaseStatus.PendingQC &&
    !histoCase.caseWithStatus.onHold ? (
      <Button appearance="prominent" iconAfter="check" block={true} onClick={onTransition}>
        Pass QC
      </Button>
    ) : loggedInUser.can([RolePermissions.AP_CaseViewer_MarkCaseasScored]) &&
      // A reader works a Processed case. The backend only gives a reader a Pending Adjudication
      // case (the reader case-with-images variant) when they are its adjudicator (LUC-319).
      (histoCase.caseWithStatus.status === CaseStatus.Processed ||
        (histoCase.caseWithStatus.status === CaseStatus.PendingAdjudication &&
          "readCompletedAt" in histoCase)) &&
      // A Forced adjudicator selects a winning read and has no read of their own to score.
      !isForcedAdjudicator &&
      !histoCase.caseWithStatus.onHold &&
      // Only offer "Mark as Scored" while this reader's own read is still open. Once
      // readCompletedAt is set the read is locked, and re-submitting would 409. The field lives on
      // the reader case-with-images variant only.
      !("readCompletedAt" in histoCase && histoCase.readCompletedAt !== null) ? (
      <Button appearance="prominent" iconAfter="check" block={true} onClick={onCompleteRead}>
        Mark as Scored
      </Button>
    ) : "errorMessage" in histoCase ? (
      <Callout intent="danger">
        <Text as="p">Unable to transition case status</Text>
      </Callout>
    ) : null;

  const userCanEditCaseStatus = loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]);
  const userCanPutOnHoldCases = loggedInUser.can([RolePermissions.IM_CaseListTab_PutoHold]);

  const caseStatusDialog = userCanEditCaseStatus ? <CaseStatusDialog /> : null;
  const casePutOnHoldDialog = userCanPutOnHoldCases ? <PutOnHoldCaseDialog /> : null;
  const caseRemoveHoldDialog = userCanPutOnHoldCases ? <RemoveHoldCaseDialog /> : null;

  const isAdminCase = "siteId" in histoCase.caseWithStatus;

  const editIcon = loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]) && (
    <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
      <Icon name="menu" />
      <TooltipFlyout placement={"left-start"}>
        <TooltipLink
          onClick={() =>
            dispatch(
              openEditCaseDialog({
                id: histoCase.caseWithStatus.id,
                studyId: histoCase.caseWithStatus.studyId,
                adjudicationParadigm:
                  "resource" in studyView ? studyView.resource.study.adjudicationParadigm : null,
                adjudicationMode:
                  "resource" in studyView
                    ? studyView.resource.study.adjudicationMode
                    : AdjudicationMode.CaseLevel,
                refreshSingleCaseOnSuccess: true
              })
            )
          }
        >
          Edit Case Data
        </TooltipLink>
        <TooltipLink
          onClick={() =>
            isAdminCase ? dispatch(openCaseStatusDialog(histoCase.caseWithStatus)) : null
          }
        >
          Revert Case
        </TooltipLink>
        {userCanPutOnHoldCases && isAdminCase && !histoCase.caseWithStatus.onHold && (
          <TooltipLink onClick={() => dispatch(openPutOnHoldCaseDialog(histoCase.caseWithStatus))}>
            Put on Hold
          </TooltipLink>
        )}
        {userCanPutOnHoldCases && isAdminCase && histoCase.caseWithStatus.onHold && (
          <TooltipLink onClick={() => dispatch(openRemoveHoldCaseDialog(histoCase.caseWithStatus))}>
            Remove Hold
          </TooltipLink>
        )}
      </TooltipFlyout>
    </Button>
  );

  function imageQueriesWereClosed(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Closed);
  }

  function imageQueriesNeedResolution(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Open);
  }

  function inQueryChain(imageAndQuery: ImageAndQuery): boolean {
    if ("imageQueries" in histoCase) {
      const allImageQueries: readonly ImageAndQueries[] = histoCase.imageQueries || [];
      const imageQueries: ImageAndQueries =
        allImageQueries.find(iq => {
          return iq.length > 0 && iq.find(x => x.image.id == imageAndQuery.image.id) != null;
        }) || [];
      return imageQueriesNeedResolution(imageQueries) || imageQueriesWereClosed(imageQueries);
    } else {
      return false;
    }
  }

  return (
    <>
      <Box display="flex" width="100%">
        <Heading textAlign="left" mb={2}>
          <div
            style={{
              display: "flex",
              color: "#231F20",
              height: "30px",
              fontWeight: "700",
              fontSize: "20px"
            }}
          >
            <div style={{ paddingBottom: "0px", width: "60px" }}>Case&nbsp;</div>
            <div style={{ height: "20px" }}>
              {histoCase.caseWithStatus.onHold && <RedLabel>ON HOLD</RedLabel>}
            </div>
          </div>
        </Heading>
        <Box ml={"auto"}>
          {editIcon}
          <CollapseIcon onToggle={() => dispatch(toggleSidebarExpanded())} />
        </Box>
      </Box>
      <CompactTable compact={true} className="image-info-table">
        <tbody>
          <tr>
            <th>Histo Procedure ID</th>
          </tr>
          <tr>
            <td>
              <Box>{histoCase.caseWithStatus.histoProcedureId}</Box>
            </td>
          </tr>
          <tr>
            <th>Endo Procedure ID</th>
          </tr>
          <tr>
            <td>
              <Box>{histoCase.caseWithStatus.procId}</Box>
            </td>
          </tr>
          {"subjectId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewSubjectID]) && (
              <>
                <tr>
                  <th>Subject ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.subjectId}</Box>
                  </td>
                </tr>
              </>
            )}
          {"siteId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewSiteID]) && (
              <>
                <tr>
                  <th>Site ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.siteId}</Box>
                  </td>
                </tr>
              </>
            )}
          {"visitId" in histoCase.caseWithStatus &&
            loggedInUser.can([RolePermissions.AP_CaseData_ViewVisitID]) && (
              <>
                <tr>
                  <th>Visit ID</th>
                </tr>
                <tr>
                  <td>
                    <Box>{histoCase.caseWithStatus.visitId}</Box>
                  </td>
                </tr>
              </>
            )}
          <tr>
            <th>Case Created</th>
          </tr>
          <tr>
            <td>
              <Box>
                <Timestamp date={histoCase.caseWithStatus.createdAt} />
              </Box>
            </td>
          </tr>
          <tr>
            <th>Case Status</th>
          </tr>
          <tr>
            <td>
              <Box>{formatCaseStatus(histoCase.caseWithStatus.status)}</Box>
            </td>
          </tr>
          {"readers" in histoCase ? (
            <>
              <tr>
                <th>CR Assigned</th>
              </tr>
              <tr>
                <td>
                  {histoCase.readers.length ? (
                    <span>
                      {histoCase.readers.slice(0, 5).map(readerStats => (
                        <Box key={readerStats.reader.id} ml="1px" mr="1px" display="inline">
                          <Avatar isSmall={true} user={readerStats.reader} />
                        </Box>
                      ))}
                      {histoCase.readers.length > 5 ? "…" : null}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            </>
          ) : null}
          <tr>
            <th>Study Name</th>
          </tr>
          <tr>
            <td>
              <Box>{studyName}</Box>
            </td>
          </tr>
        </tbody>
      </CompactTable>
      {"resource" in studyView &&
      studyView.resource.study.adjudicationParadigm === AdjudicationParadigm.Hybrid &&
      studyView.resource.study.enableImageQualityAssessment &&
      selectedImage !== null &&
      imageQualityAssessment !== null ? (
        "resource" in imageQualityAssessment ? (
          // Render once this image's saved IQA (or null) has loaded. The `key` includes a hash of the
          // loaded resource so the form remounts — re-seeding its local fields — both when the
          // selected image changes and after a save round-trips (pending → resource with the values).
          <ImageQualityAssessmentForm
            key={`${selectedImage.image.id}:${JSON.stringify(imageQualityAssessment.resource)}`}
            caseId={histoCase.caseWithStatus.id}
            imageId={selectedImage.image.id}
            savedAssessment={imageQualityAssessment.resource}
            deficientFeatureOptions={deficientFeatureOptions}
            isHeStain={isHeStain(selectedImage.image.stainType)}
          />
        ) : "errorMessage" in imageQualityAssessment ? (
          // IQA applies to this study/image but its fetch failed. Surface a callout so the reader
          // knows the assessment couldn't be loaded (vs. it silently not being shown), matching the
          // save-error pattern in the form itself.
          <Callout intent="danger" style={{ marginBottom: "0.4rem" }}>
            Unable to load the image quality assessment. Please try again.
          </Callout>
        ) : null
      ) : null}
      <div style={{ marginBottom: "0.2rem" }}>{caseQcButton}</div>
      {confirmCaseQCTransitionDialog}
      {caseStatusDialog}
      {casePutOnHoldDialog}
      {caseRemoveHoldDialog}

      {selectedImage !== null ? (
        <>
          <hr />
          <ImageInfo
            studyName={null}
            imageAndQuery={selectedImage}
            simpleCase={null}
            inQueryChain={
              inQueryChain(
                selectedImage
              ) /*"imageQueries" in histoCase && histoCase.imageQueries.length > 0 || selectedImage.image.lastLabQueryId != null*/
            }
          />
        </>
      ) : null}
    </>
  );
};

export default SidebarPanelCase;
